import { useState, useEffect } from 'react';

/**
 * Contact sheet content (non-modal version for right pane integration)
 */
function ContactSheetContent({
  extractedPages,
  pageExceptions,
  onPageClick,
  currentPageIndex,
  onPreservePages,
  processedPagesCache,
  filters,
  getEffectiveFilters,
  onProcessPages,
  savedPresets,
  onApplyPreset,
  selectedPages = new Set(), // Now comes from parent
  setSelectedPages, // Now controlled by parent
  showOriginal = true, // Control whether to show original images in comparison
  onToggleShowOriginal, // Handler for Show Original toggle
}) {
  const [selectionMode, setSelectionMode] = useState(true); // Default to select mode ON
  const [processingPages, setProcessingPages] = useState(new Set());
  const [thumbnailSize, setThumbnailSize] = useState(3); // 1-8 scale (tiny to huge)

  // Page visibility filters - organized by adjustment type
  const [showNoAdjustment, setShowNoAdjustment] = useState(true);
  const [showPreserved, setShowPreserved] = useState(true);
  const [showGlobalAdjustment, setShowGlobalAdjustment] = useState(true);
  const [showPresetAdjustment, setShowPresetAdjustment] = useState(true);
  const [showCustomAdjustment, setShowCustomAdjustment] = useState(true);
  const [selectedPresets, setSelectedPresets] = useState(new Set()); // Set of selected preset names (empty = all)
  const [presetDropdownOpen, setPresetDropdownOpen] = useState(false);

  // Trigger processing for all pages on mount (only for pages with actual adjustments)
  useEffect(() => {
    console.log('[ContactSheetContent] useEffect triggered');
    console.log('[ContactSheetContent] processedPagesCache has', Object.keys(processedPagesCache || {}).length, 'entries');
    if (onProcessPages && getEffectiveFilters) {
      // Find pages that need processing
      const pagesToProcess = [];

      extractedPages.forEach((page, index) => {
        const pageNumber = index + 1;

        const effectiveFilters = getEffectiveFilters(pageNumber);
        console.log(`[ContactSheetContent] Page ${pageNumber} effectiveFilters:`, effectiveFilters);

        // Skip preserved pages - they don't need processing UNLESS they have preview filters
        const isPreserved = pageExceptions?.[pageNumber]?.preserveOriginal;

        // Check if this is identity filters (no adjustments)
        const isIdentityFilters =
          !effectiveFilters.invert &&
          !effectiveFilters.grayscale &&
          effectiveFilters.brightness === 100 &&
          effectiveFilters.contrast === 100 &&
          !effectiveFilters.removeBackground;

        // Skip pages with identity/default filters (no adjustments)
        // For preserved pages: they get processed if they have preview filters
        // For non-preserved pages: they get processed if they have any adjustments
        if (isIdentityFilters) {
          console.log(`[ContactSheetContent] Page ${pageNumber} has identity filters, skipping`);
          return;
        }
        console.log(`[ContactSheetContent] Page ${pageNumber} has adjustments, will process`);

        const cacheKey = `${index}-${JSON.stringify(effectiveFilters)}`;

        // Check if this page is already cached
        if (!processedPagesCache?.[cacheKey]) {
          pagesToProcess.push({ pageIndex: index, filters: effectiveFilters });
        }
      });

      console.log(`[ContactSheetContent] Found ${pagesToProcess.length} pages to process:`, pagesToProcess.map(p => p.pageIndex + 1));

      if (pagesToProcess.length > 0) {
        // Mark pages as processing
        const processingSet = new Set(pagesToProcess.map(p => p.pageIndex));
        setProcessingPages(processingSet);

        // Trigger batch processing
        onProcessPages(pagesToProcess).then(() => {
          // Clear processing state when done
          setProcessingPages(new Set());
        }).catch((error) => {
          console.error('Error processing pages:', error);
          setProcessingPages(new Set());
        });
      }
    }
  }, [extractedPages, processedPagesCache, getEffectiveFilters, onProcessPages, pageExceptions, filters]);

  // Add keyboard shortcut for Select All (Cmd+A / Ctrl+A)
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check for Cmd/Ctrl+A
      if ((event.metaKey || event.ctrlKey) && event.key === 'a') {
        // Prevent default browser behavior (select all text)
        event.preventDefault();
        // Select all pages
        selectAll();
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [extractedPages]); // Re-run if extractedPages changes

  // Close preset dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (presetDropdownOpen && !event.target.closest('.preset-dropdown-container')) {
        setPresetDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [presetDropdownOpen]);

  const togglePageSelection = (pageNumber) => {
    const newSelection = new Set(selectedPages);
    if (newSelection.has(pageNumber)) {
      newSelection.delete(pageNumber);
    } else {
      newSelection.add(pageNumber);
    }
    setSelectedPages(newSelection);
  };

  const selectAll = () => {
    const allPages = new Set(Array.from({ length: extractedPages.length }, (_, i) => i + 1));
    setSelectedPages(allPages);
  };

  const clearSelection = () => {
    setSelectedPages(new Set());
  };

  const handlePreserveSelected = () => {
    if (selectedPages.size === 0) {
      alert('No pages selected');
      return;
    }

    // Check if all selected pages are already preserved
    const allPreserved = Array.from(selectedPages).every(pageNumber => {
      return pageExceptions?.[pageNumber]?.preserveOriginal;
    });

    if (allPreserved) {
      // Unpreserve if all are already preserved
      if (confirm(`Remove preserve setting from ${selectedPages.size} page(s)?`)) {
        onPreservePages(Array.from(selectedPages), false);
        clearSelection();
        setSelectionMode(false);
      }
    } else {
      // Preserve if not all are preserved
      if (confirm(`Preserve ${selectedPages.size} page(s) as original?`)) {
        onPreservePages(Array.from(selectedPages), true);
        clearSelection();
        setSelectionMode(false);
      }
    }
  };

  const handleUnpreserveSelected = () => {
    if (selectedPages.size === 0) {
      alert('No pages selected');
      return;
    }
    onPreservePages(Array.from(selectedPages), false);
    clearSelection();
    setSelectionMode(false);
  };

  const handleApplyPreset = (presetId) => {
    if (selectedPages.size === 0) {
      alert('No pages selected');
      return;
    }
    if (onApplyPreset) {
      onApplyPreset(presetId, Array.from(selectedPages));
      clearSelection();
      setSelectionMode(false);
    }
  };

  const handlePageClick = (index, event) => {
    const pageNumber = index + 1;

    // Check for modifier keys (Cmd on Mac, Ctrl on Windows/Linux)
    const isMultiSelectKey = event.metaKey || event.ctrlKey;

    // Always toggle selection: add if not selected, remove if selected
    // This allows clicking multiple cards to select them, and clicking selected cards to deselect
    togglePageSelection(pageNumber);
  };

  // Check if global filters have any adjustments applied
  const hasGlobalAdjustments = () => {
    const defaultFilters = {
      invert: false,
      grayscale: false,
      brightness: 100,
      contrast: 100,
      removeBackground: false,
      bgRemovalTolerance: 0.15,
      bgSoftenEdges: false,
    };

    return Object.keys(defaultFilters).some(key => {
      return filters[key] !== defaultFilters[key];
    });
  };

  const globalHasAdjustments = hasGlobalAdjustments();

  // Filter pages based on visibility settings
  const getFilteredPages = () => {
    return extractedPages.filter((page, index) => {
      const pageNumber = index + 1;
      const exception = pageExceptions?.[pageNumber];
      const isPreserved = exception?.preserveOriginal;

      // Categorize the page
      if (isPreserved) {
        return showPreserved;
      }

      // Check if page has exception
      if (exception?.enabled) {
        // Has exception - check if it's Global, Preset, or Custom
        if (exception?.useGlobal) {
          // Global adjustment (explicitly set)
          return showGlobalAdjustment;
        } else if (exception?.label) {
          // Preset adjustment - check preset filter
          if (!showPresetAdjustment) return false;
          // If no specific presets selected, show all presets
          if (selectedPresets.size === 0) return true;
          // Otherwise, only show if this preset is selected
          return selectedPresets.has(exception.label);
        } else {
          // Custom adjustment (no label, no useGlobal)
          return showCustomAdjustment;
        }
      } else {
        // No exception - check if global has adjustments or is default
        if (globalHasAdjustments) {
          // Global has adjustments applied
          return showGlobalAdjustment;
        } else {
          // No adjustments at all (default state)
          return showNoAdjustment;
        }
      }
    });
  };

  const filteredPages = getFilteredPages();

  // Count pages by adjustment type for filter labels
  const getPageCounts = () => {
    let noAdjustmentCount = 0;
    let preservedCount = 0;
    let globalCount = 0;
    let presetCount = 0;
    let customCount = 0;
    const presetCounts = {}; // { presetName: count }

    extractedPages.forEach((page, index) => {
      const pageNumber = index + 1;
      const exception = pageExceptions?.[pageNumber];
      const isPreserved = exception?.preserveOriginal;

      // Categorize the page
      if (isPreserved) {
        preservedCount++;
      } else if (exception?.enabled) {
        // Has exception - check if it's Global, Preset, or Custom
        if (exception?.useGlobal) {
          globalCount++;
        } else if (exception?.label) {
          presetCount++;
          // Track individual preset counts
          const presetName = exception.label;
          presetCounts[presetName] = (presetCounts[presetName] || 0) + 1;
        } else {
          customCount++;
        }
      } else {
        // No exception - check if global has adjustments or is default
        if (globalHasAdjustments) {
          globalCount++;
        } else {
          noAdjustmentCount++;
        }
      }
    });

    return { noAdjustmentCount, preservedCount, globalCount, presetCount, customCount, presetCounts };
  };

  const { noAdjustmentCount, preservedCount, globalCount, presetCount, customCount, presetCounts } = getPageCounts();

  // Check if all filters are showing (no filters active)
  const allFiltersShowing = () => {
    return showNoAdjustment && showPreserved && showGlobalAdjustment &&
           showPresetAdjustment && showCustomAdjustment && selectedPresets.size === 0;
  };

  // Toggle all filters - show all if any are hidden, hide all if all are showing
  const toggleAllFilters = () => {
    if (allFiltersShowing()) {
      // All showing - hide all
      setShowNoAdjustment(false);
      setShowPreserved(false);
      setShowGlobalAdjustment(false);
      setShowPresetAdjustment(false);
      setShowCustomAdjustment(false);
    } else {
      // Some hidden - show all
      setShowNoAdjustment(true);
      setShowPreserved(true);
      setShowGlobalAdjustment(true);
      setShowPresetAdjustment(true);
      setShowCustomAdjustment(true);
      setSelectedPresets(new Set());
    }
  };

  // Check if any filters are active (hiding content)
  const hasActiveFilters = () => {
    return !showNoAdjustment ||
           !showPreserved ||
           !showGlobalAdjustment ||
           !showPresetAdjustment ||
           !showCustomAdjustment ||
           selectedPresets.size > 0;
  };

  // Map thumbnail size (1-8) to grid column classes
  const getGridColumns = () => {
    switch(thumbnailSize) {
      case 1: return 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8';
      case 2: return 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7';
      case 3: return 'grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'; // Mobile 3-state: 3 columns
      case 4: return 'grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4';
      case 5: return 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3'; // Mobile 3-state: 2 columns
      case 6: return 'grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2';
      case 7: return 'grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2';
      case 8: return 'grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-1'; // Mobile 3-state: 1 column
      default: return 'grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5';
    }
  };

  // Map of slider positions to actual thumbnail sizes (skipping size 7 which duplicates 6)
  const sliderToSize = [1, 2, 3, 4, 5, 6, 8]; // 7 positions
  const sizeToSlider = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 4, 6: 5, 7: 5, 8: 6 }; // Map size to slider index

  // Get desktop columns count for current thumbnail size
  const getDesktopColumns = (size) => {
    // Assuming large desktop (lg breakpoint)
    switch(size) {
      case 1: return 8;
      case 2: return 7;
      case 3: return 5;
      case 4: return 4;
      case 5: return 3;
      case 6: return 2;
      case 7: return 2; // Same as 6
      case 8: return 1;
      default: return 5;
    }
  };

  // Calculate approximate pages visible per viewport (columns × ~2-3 rows typical)
  const getApproxPagesVisible = (size) => {
    const cols = getDesktopColumns(size);
    // Estimate 2.5 rows visible on average desktop viewport
    return Math.round(cols * 2.5);
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-white dark:bg-gray-800">
      {/* Header Controls - Single Row */}
      <div className="p-1 md:p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center justify-between gap-1">
          {/* Left Side: Page count and selected count */}
          <div className="md:hidden flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 min-w-0">
            {/* Page count with icon */}
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="font-medium whitespace-nowrap">{filteredPages.length}/{extractedPages.length}</span>
            </div>

            {/* Selected count with icon */}
            {selectedPages.size > 0 && (
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium whitespace-nowrap">{selectedPages.size}</span>
              </div>
            )}
          </div>

          {/* Desktop: Left buttons and right-aligned filters */}
          <div className="hidden md:flex items-center gap-2 flex-1">
            {/* Left Side: Select All + Select None (Show Original moved to top header) */}
            <div className="flex items-center gap-2">
              {/* Select All Button */}
              <button
                onClick={selectAll}
                className="p-2 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors border border-gray-300 dark:border-gray-600"
                title="Select All"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>

              {/* Select None Button */}
              <button
                onClick={clearSelection}
                className="p-2 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={selectedPages.size === 0}
                title="Clear Selection"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Page count and selected count */}
            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
              {/* Page count with icon */}
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="font-medium">{filteredPages.length}/{extractedPages.length}</span>
              </div>

              {/* Selected count with icon */}
              {selectedPages.size > 0 && (
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">{selectedPages.size}</span>
                </div>
              )}
            </div>

            {/* Right Side: Filters (right-aligned) */}
            <div className="ml-auto flex items-center gap-1.5 overflow-visible">
              {/* Outer panel grouping filter icon, buttons, and clear button */}
              <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg p-1 overflow-visible">
                {/* Eye icon for visibility/show controls */}
                <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>

                {/* Filter buttons container */}
                <div className="flex items-center gap-1.5 overflow-visible">
                  {/* No Adjustment Filter */}
                  <button
                    onClick={() => setShowNoAdjustment(!showNoAdjustment)}
                    className={`relative px-1.5 py-0.5 rounded-md transition-colors flex flex-col items-center justify-center gap-0.5 min-w-[36px] h-[31px] ${
                      showNoAdjustment
                        ? 'bg-gray-300 dark:bg-gray-500 text-gray-800 dark:text-gray-100 shadow-md'
                        : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                    title="No Adjustment"
                  >
                    {/* Badge count */}
                    {noAdjustmentCount > 0 && (
                      <span className="absolute -top-0.5 -left-0.5 bg-red-500 text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5 z-20">
                        {noAdjustmentCount}
                      </span>
                    )}
                    {/* Icon */}
                    <span className="text-sm leading-none">📄</span>
                    {/* Label */}
                    <span className="text-[7px] font-medium leading-tight">None</span>
                  </button>

                  {/* Preserved Filter */}
                  <button
                    onClick={() => setShowPreserved(!showPreserved)}
                    className={`relative px-1.5 py-0.5 rounded-md transition-colors flex flex-col items-center justify-center gap-0.5 min-w-[36px] h-[31px] ${
                      showPreserved
                        ? 'bg-gray-300 dark:bg-gray-500 text-gray-800 dark:text-gray-100 shadow-md'
                        : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                    title="Preserved"
                  >
                    {/* Badge count */}
                    {preservedCount > 0 && (
                      <span className="absolute -top-0.5 -left-0.5 bg-red-500 text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5 z-20">
                        {preservedCount}
                      </span>
                    )}
                    {/* Icon */}
                    <span className="text-sm leading-none">🔒</span>
                    {/* Label */}
                    <span className="text-[7px] font-medium leading-tight">Preserve</span>
                  </button>

                  {/* Global Adjustment Filter */}
                  <button
                    onClick={() => setShowGlobalAdjustment(!showGlobalAdjustment)}
                    className={`relative px-1.5 py-0.5 rounded-md transition-colors flex flex-col items-center justify-center gap-0.5 min-w-[36px] h-[31px] ${
                      showGlobalAdjustment
                        ? 'bg-gray-300 dark:bg-gray-500 text-gray-800 dark:text-gray-100 shadow-md'
                        : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                    title="Global Adjustment"
                  >
                    {/* Badge count */}
                    {globalCount > 0 && (
                      <span className="absolute -top-0.5 -left-0.5 bg-red-500 text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5 z-20">
                        {globalCount}
                      </span>
                    )}
                    {/* Icon */}
                    <span className="text-sm leading-none">🌐</span>
                    {/* Label */}
                    <span className="text-[7px] font-medium leading-tight">Global</span>
                  </button>

                  {/* Preset Adjustment Filter with Dropdown */}
                  <div className="preset-dropdown-container relative flex items-center">
                    <div className={`relative flex items-center rounded-md h-[31px] ${
                      showPresetAdjustment
                        ? 'bg-gray-300 dark:bg-gray-500 shadow-md'
                        : 'bg-white dark:bg-gray-600'
                    }`}>
                      {/* Main preset button */}
                      <button
                        onClick={() => setShowPresetAdjustment(!showPresetAdjustment)}
                        className={`relative px-1.5 py-0.5 transition-colors flex flex-col items-center justify-center gap-0.5 min-w-[36px] h-full overflow-visible ${
                          showPresetAdjustment
                            ? 'text-gray-800 dark:text-gray-100'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                        title="Preset Adjustment"
                      >
                        {/* Badge count */}
                        {presetCount > 0 && (
                          <span className="absolute -top-0.5 -left-0.5 bg-red-500 text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5 z-20">
                            {presetCount}
                          </span>
                        )}
                        {/* Icon */}
                        <span className="text-sm leading-none">💾</span>
                        {/* Label */}
                        <span className="text-[7px] font-medium leading-tight">Preset</span>
                      </button>

                      {/* Vertical separator and dropdown trigger (only when active and has presets) */}
                      {showPresetAdjustment && presetCount > 0 && (
                        <>
                          <div className="h-full w-[1px] bg-gray-400 dark:bg-gray-600"></div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPresetDropdownOpen(!presetDropdownOpen);
                            }}
                            className="px-1.5 h-full text-gray-800 dark:text-gray-100 hover:bg-gray-400 dark:hover:bg-gray-600 transition-colors flex items-center justify-center"
                            title="Filter by specific presets"
                          >
                            <span className="text-[10px]">▼</span>
                          </button>
                        </>
                      )}
                    </div>

                    {/* Dropdown Menu with Checkboxes */}
                    {showPresetAdjustment && presetCount > 0 && presetDropdownOpen && (
                      <div className="absolute top-full right-0 mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg z-50 min-w-[200px]">
                        <div className="p-2 max-h-64 overflow-y-auto">
                          {/* Select All / Clear All */}
                          <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200 dark:border-gray-600">
                            <button
                              onClick={() => setSelectedPresets(new Set())}
                              className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                            >
                              All
                            </button>
                            <button
                              onClick={() => setSelectedPresets(new Set(Object.keys(presetCounts)))}
                              className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                            >
                              None
                            </button>
                          </div>

                          {/* Preset Checkboxes */}
                          {Object.keys(presetCounts).sort().map(presetName => {
                            const isSelected = selectedPresets.has(presetName);
                            return (
                              <label
                                key={presetName}
                                className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-600 rounded cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    const newSelected = new Set(selectedPresets);
                                    if (e.target.checked) {
                                      newSelected.add(presetName);
                                    } else {
                                      newSelected.delete(presetName);
                                    }
                                    setSelectedPresets(newSelected);
                                  }}
                                  className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
                                />
                                <span className="text-xs text-gray-900 dark:text-gray-100 flex-1">
                                  {presetName}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  ({presetCounts[presetName]})
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Custom Adjustments Filter */}
                  <button
                    onClick={() => setShowCustomAdjustment(!showCustomAdjustment)}
                    className={`relative px-1.5 py-0.5 rounded-md transition-colors flex flex-col items-center justify-center gap-0.5 min-w-[36px] h-[31px] ${
                      showCustomAdjustment
                        ? 'bg-gray-300 dark:bg-gray-500 text-gray-800 dark:text-gray-100 shadow-md'
                        : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                    title="Custom Adjustments"
                  >
                    {/* Badge count */}
                    {customCount > 0 && (
                      <span className="absolute -top-0.5 -left-0.5 bg-red-500 text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5 z-20">
                        {customCount}
                      </span>
                    )}
                    {/* Icon */}
                    <span className="text-sm leading-none">🎯</span>
                    {/* Label */}
                    <span className="text-[7px] font-medium leading-tight">Custom</span>
                  </button>
                </div>

                {/* Toggle All/None Button */}
                <button
                  onClick={toggleAllFilters}
                  className="p-1.5 bg-white dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 rounded-lg transition-colors"
                  title={allFiltersShowing() ? "Hide all page types" : "Show all page types"}
                >
                  {allFiltersShowing() ? (
                    // Eye with slash (hide all)
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    // Grid/boxes (show all)
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Right Side: All buttons in a row */}
          <div className="md:hidden flex items-center gap-0.5 shrink-0">
            {/* Show Original Group */}
            {onToggleShowOriginal && (
              <button
                onClick={onToggleShowOriginal}
                className={`p-2 rounded-md transition-colors ${
                  showOriginal
                    ? 'bg-indigo-600 dark:bg-indigo-500 text-white shadow-md'
                    : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
                title={showOriginal ? "Hide Original" : "Show Original"}
              >
                {/* Stacked boxes icon (box over line over box - like division symbol) */}
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  {/* Top box - farther from line for better spacing */}
                  <rect x="4" y="0" width="16" height="8" rx="1" />
                  {/* Middle line */}
                  <rect x="3" y="11" width="18" height="2" rx="1" />
                  {/* Bottom box - farther from line for better spacing */}
                  <rect x="4" y="16" width="16" height="8" rx="1" />
                </svg>
              </button>
            )}

            {/* Vertical separator */}
            <div className="h-8 w-0.5 bg-gray-400 dark:bg-gray-500 mx-1"></div>

            {/* Grid selector buttons group */}
            <div className="flex items-center">
              {/* 3 columns (Small) */}
              <button
                onClick={() => setThumbnailSize(3)}
                className={`p-2 rounded-md transition-colors ${
                  thumbnailSize <= 4
                    ? 'bg-indigo-600 dark:bg-indigo-500 text-white shadow-md'
                    : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
                title="Small (3 columns)"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="2" y="4" width="5" height="16" rx="1" />
                  <rect x="9.5" y="4" width="5" height="16" rx="1" />
                  <rect x="17" y="4" width="5" height="16" rx="1" />
                </svg>
              </button>

              {/* 2 columns (Medium) */}
              <button
                onClick={() => setThumbnailSize(5)}
                className={`p-2 rounded-md transition-colors ${
                  thumbnailSize === 5 || thumbnailSize === 6
                    ? 'bg-indigo-600 dark:bg-indigo-500 text-white shadow-md'
                    : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
                title="Medium (2 columns)"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="2" y="4" width="8.5" height="16" rx="1" />
                  <rect x="13.5" y="4" width="8.5" height="16" rx="1" />
                </svg>
              </button>

              {/* 1 column (Large) */}
              <button
                onClick={() => setThumbnailSize(8)}
                className={`p-2 rounded-md transition-colors ${
                  thumbnailSize >= 7
                    ? 'bg-indigo-600 dark:bg-indigo-500 text-white shadow-md'
                    : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                }`}
                title="Large (1 column)"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="2" y="4" width="20" height="16" rx="1" />
                </svg>
              </button>
            </div>

            {/* Vertical separator */}
            <div className="h-8 w-0.5 bg-gray-400 dark:bg-gray-500 mx-1"></div>

            {/* Select All/Clear Buttons group */}
            <div className="flex items-center">
              <button
                onClick={selectAll}
                className="p-2 bg-white dark:bg-gray-600 hover:bg-gray-100 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-300 rounded-md transition-colors"
                title="Select All"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              <button
                onClick={clearSelection}
                className="p-2 bg-white dark:bg-gray-600 hover:bg-gray-100 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-300 rounded-md transition-colors"
                disabled={selectedPages.size === 0}
                title="Clear Selection"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Page Grid - Scrollable */}
      <div className="flex-1 min-h-0 overflow-y-auto p-1 md:p-3">
        {/* Empty state when filters hide all pages */}
        {filteredPages.length === 0 && extractedPages.length > 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <svg className="w-16 h-16 text-gray-400 dark:text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <p className="text-lg font-medium text-gray-500 dark:text-gray-400 mb-2">
              No pages match the current filters
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Adjust the filter buttons above to show pages
            </p>
          </div>
        )}

        <div className={`grid ${getGridColumns()} gap-4 sm:gap-5 items-start`}>
          {filteredPages.map((page, originalIndex) => {
            const index = extractedPages.indexOf(page);
            const pageNumber = index + 1;
            const exception = pageExceptions[pageNumber];
            const isPreserved = exception?.preserveOriginal;
            const hasException = exception?.enabled && !isPreserved;
            const isCurrentPage = index === currentPageIndex;
            const exceptionLabel = exception?.label;
            const isSelected = selectedPages.has(pageNumber);

            // Get both original and processed images
            const originalUrl = page.imageDataUrl;
            let processedUrl = null;
            let showProcessing = false;
            let hasIdentityFilters = false;

            // Get processed image (even for preserved pages if they have preview filters)
            if (getEffectiveFilters) {
              const effectiveFilters = getEffectiveFilters(pageNumber);

              // Check if filters are identity/default (no adjustments)
              hasIdentityFilters =
                !effectiveFilters.invert &&
                !effectiveFilters.grayscale &&
                effectiveFilters.brightness === 100 &&
                effectiveFilters.contrast === 100 &&
                !effectiveFilters.removeBackground;

              if (!hasIdentityFilters) {
                // Only get processed version for pages with actual adjustments
                const cacheKey = `${index}-${JSON.stringify(effectiveFilters)}`;
                const processedPage = processedPagesCache?.[cacheKey];

                console.log(`[Render] Page ${index + 1} cacheKey:`, cacheKey.substring(0, 100));
                console.log(`[Render] Page ${index + 1} processedPage found:`, !!processedPage);

                if (processedPage?.processedDataUrl) {
                  processedUrl = processedPage.processedDataUrl;
                  console.log(`[Render] Page ${index + 1} using processed image`);
                } else if (processingPages.has(index)) {
                  showProcessing = true;
                  console.log(`[Render] Page ${index + 1} showing processing indicator`);
                } else {
                  console.log(`[Render] Page ${index + 1} NO processed image found, using original`);
                }
              }
            }

            return (
              <div
                key={index}
                onClick={(e) => handlePageClick(index, e)}
                className={`
                  cursor-pointer rounded-lg overflow-hidden border-2 transition-all
                  shadow-md hover:shadow-lg
                  flex flex-col
                  ${isSelected
                    ? 'bg-white dark:bg-gray-800 border-indigo-600 dark:border-indigo-400 shadow-xl'
                    : isPreserved
                    ? 'bg-gray-200 dark:bg-gray-900 border-amber-400 dark:border-amber-400 shadow-lg'
                    : hasException && exception?.useGlobal
                    ? 'bg-gray-200 dark:bg-gray-900 border-blue-400 dark:border-blue-400 shadow-lg'
                    : hasException && !exception?.useGlobal && exceptionLabel
                    ? 'bg-gray-200 dark:bg-gray-900 border-purple-400 dark:border-purple-400 shadow-lg'
                    : hasException
                    ? 'bg-gray-200 dark:bg-gray-900 border-orange-400 dark:border-orange-400 shadow-lg'
                    : 'bg-gray-200 dark:bg-gray-900 border-gray-300 dark:border-gray-500 hover:border-indigo-400 dark:hover:border-indigo-400'
                  }
                `}
              >
                {/* Card Header with Page Number */}
                <div className={`relative z-10 px-1.5 py-0.5 flex items-center justify-between ${
                  isSelected
                    ? 'bg-gradient-to-br from-indigo-600 to-indigo-700 dark:from-indigo-500 dark:to-indigo-600'
                    : 'bg-gradient-to-br from-gray-600 to-gray-700 dark:from-gray-700 dark:to-gray-800'
                }`}>
                  <div className="flex items-center gap-1 min-w-0 flex-1">
                    {/* Page icon on mobile, "Page" text on desktop */}
                    <svg className="w-3 h-3 md:w-4 md:h-4 md:hidden text-white flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm md:text-base font-bold text-white whitespace-nowrap truncate">
                      {pageNumber}
                    </p>
                  </div>
                  {/* Selection Checkbox - Only show when selected */}
                  {isSelected && (
                    <div className="w-4 h-4 md:w-5 md:h-5 rounded bg-white flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 md:w-3.5 md:h-3.5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Page Images - Stacked Vertically or Single Image */}
                <div className={`${showOriginal && (!isPreserved || processedUrl || showProcessing) ? 'aspect-[8.5/11] bg-gray-100 dark:bg-gray-900' : isPreserved && hasIdentityFilters ? '' : 'flex-1'} relative`}>
                  {showOriginal && (!isPreserved || processedUrl || showProcessing) ? (
                    /* Show original and final stacked vertically when showOriginal is true AND page has adjustments */
                    <div className="w-full h-full flex flex-col">
                      {/* Original (Top) */}
                      <div className="flex-1 relative border-b border-gray-300 dark:border-gray-600 overflow-hidden">
                        <img
                          src={originalUrl}
                          alt={`Page ${pageNumber} (Original)`}
                          className="w-full h-full object-contain"
                        />
                        {/* Top-left pill - Original */}
                        <div className="absolute top-1 left-1">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-red-100 dark:bg-red-900/50 text-[10px] text-red-700 dark:text-red-300 font-semibold shadow-sm">
                            Original
                          </span>
                        </div>
                      </div>

                      {/* Final (Bottom) */}
                      <div className="flex-1 relative overflow-hidden">
                        {hasIdentityFilters ? (
                          /* Pages with no adjustments: show original again */
                          <>
                            <img
                              src={originalUrl}
                              alt={`Page ${pageNumber} (Final - No Adjustments)`}
                              className="w-full h-full object-contain"
                            />
                            {/* Top-left pill - Final */}
                            <div className="absolute top-1 left-1">
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-green-100 dark:bg-green-900/50 text-[10px] text-green-700 dark:text-green-300 font-semibold shadow-sm">
                                Final
                              </span>
                            </div>
                          </>
                        ) : showProcessing ? (
                          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">
                            <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-600 dark:border-indigo-400 border-t-transparent mb-1"></div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Processing...</p>
                          </div>
                        ) : processedUrl ? (
                          <>
                            <img
                              src={processedUrl}
                              alt={`Page ${pageNumber} (Final)`}
                              className="w-full h-full object-contain"
                            />
                            {/* Top-left pill - Final */}
                            <div className="absolute top-1 left-1">
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-green-100 dark:bg-green-900/50 text-[10px] text-green-700 dark:text-green-300 font-semibold shadow-sm">
                                Final
                              </span>
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">
                            <span className="text-2xl mb-1">⏳</span>
                            <p className="text-xs">Not processed</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : isPreserved ? (
                    /* Preserved pages (not in preview): show single image */
                    <div className="w-full relative">
                      <img
                        src={originalUrl}
                        alt={`Page ${pageNumber} (Preserved - Final)`}
                        className="w-full h-auto object-contain"
                      />
                      {/* Top-left pill - Final */}
                      <div className="absolute top-1 left-1">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-green-100 dark:bg-green-900/50 text-[10px] text-green-700 dark:text-green-300 font-semibold shadow-sm">
                          Final
                        </span>
                      </div>
                    </div>
                  ) : !showOriginal ? (
                    /* Show only Final when showOriginal is false */
                    <div className="w-full relative">
                      {hasIdentityFilters ? (
                        /* Pages with no adjustments: show original as final with pill */
                        <>
                          <img
                            src={originalUrl}
                            alt={`Page ${pageNumber} (Final - No Adjustments)`}
                            className="w-full h-auto object-contain"
                          />
                          <div className="absolute top-1 left-1">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-green-100 dark:bg-green-900/50 text-[10px] text-green-700 dark:text-green-300 font-semibold shadow-sm">
                              Final
                            </span>
                          </div>
                        </>
                      ) : showProcessing ? (
                        <div className="w-full aspect-[8.5/11] flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">
                          <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-600 dark:border-indigo-400 border-t-transparent mb-1"></div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Processing...</p>
                        </div>
                      ) : processedUrl ? (
                        <>
                          <img
                            src={processedUrl}
                            alt={`Page ${pageNumber} (Final)`}
                            className="w-full h-auto object-contain"
                          />
                          <div className="absolute top-1 left-1">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-green-100 dark:bg-green-900/50 text-[10px] text-green-700 dark:text-green-300 font-semibold shadow-sm">
                              Final
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="w-full aspect-[8.5/11] flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">
                          <span className="text-2xl mb-1">⏳</span>
                          <p className="text-xs">Not processed</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Fallback: Show only original when showOriginal is true but no adjustments */
                    <div className="w-full relative">
                      <img
                        src={originalUrl}
                        alt={`Page ${pageNumber} (Original)`}
                        className="w-full h-auto object-contain"
                      />
                      <div className="absolute top-1 left-1">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-red-100 dark:bg-red-900/50 text-[10px] text-red-700 dark:text-red-300 font-semibold shadow-sm">
                          Original
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Page Info */}
                <div className="bg-white dark:bg-gray-800 p-2 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-start">
                    <div className="flex items-center gap-2">
                      {isPreserved && (
                        <span className="text-[10px] bg-amber-500/90 text-white px-1 py-0.5 rounded font-medium whitespace-nowrap">Preserved</span>
                      )}
                      {hasException && !isPreserved && exception?.useGlobal && (
                        <span className="text-[10px] bg-blue-500/90 text-white px-1 py-0.5 rounded font-medium whitespace-nowrap">Global</span>
                      )}
                      {hasException && !isPreserved && !exception?.useGlobal && exceptionLabel && (
                        <span className="text-[10px] bg-purple-500/90 text-white px-1 py-0.5 rounded font-medium whitespace-nowrap">Preset</span>
                      )}
                      {hasException && !isPreserved && !exception?.useGlobal && !exceptionLabel && (
                        <span className="text-[10px] bg-orange-500/90 text-white px-1 py-0.5 rounded font-medium whitespace-nowrap">Custom</span>
                      )}
                      {hasIdentityFilters && !hasException && !isPreserved && (
                        <span className="text-[10px] bg-gray-500/90 text-white px-1 py-0.5 rounded font-medium whitespace-nowrap">
                          No Adjustments
                        </span>
                      )}
                    </div>
                  </div>
                  {exceptionLabel && !isPreserved && !exception?.useGlobal && (
                    <p className="text-xs text-purple-700 dark:text-purple-400 font-medium truncate mt-1 bg-purple-50 dark:bg-purple-900/30 px-2 py-0.5 rounded" title={exceptionLabel}>
                      {exceptionLabel}
                    </p>
                  )}
                  {!exceptionLabel && hasException && !isPreserved && !exception?.useGlobal && (
                    <p className="hidden md:block text-xs text-indigo-700 dark:text-indigo-400 font-medium italic mt-1 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded">
                      Custom settings
                    </p>
                  )}
                  {/* Removed "Unchanged/Original" and "Global Settings" labels - status pills are sufficient */}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer - Grid Size Control */}
      <div className="p-1 md:p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex-shrink-0">
        {/* Mobile: 3-state icon selector - HIDDEN (moved to header) */}
        <div className="hidden md:hidden flex items-center justify-center gap-2">
          {/* 3 columns (Small) */}
          <button
            onClick={() => setThumbnailSize(3)}
            className={`flex-1 px-4 py-2 rounded-lg transition-all border-2 ${
              thumbnailSize <= 4
                ? 'bg-indigo-600 dark:bg-indigo-500 border-indigo-600 dark:border-indigo-500 text-white shadow-md'
                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
            }`}
            title="Small (3 columns)"
          >
            <svg className="w-6 h-6 mx-auto" fill="currentColor" viewBox="0 0 24 24">
              <rect x="2" y="4" width="5" height="16" rx="1" />
              <rect x="9.5" y="4" width="5" height="16" rx="1" />
              <rect x="17" y="4" width="5" height="16" rx="1" />
            </svg>
          </button>

          {/* 2 columns (Medium) */}
          <button
            onClick={() => setThumbnailSize(5)}
            className={`flex-1 px-4 py-2 rounded-lg transition-all border-2 ${
              thumbnailSize === 5 || thumbnailSize === 6
                ? 'bg-indigo-600 dark:bg-indigo-500 border-indigo-600 dark:border-indigo-500 text-white shadow-md'
                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
            }`}
            title="Medium (2 columns)"
          >
            <svg className="w-6 h-6 mx-auto" fill="currentColor" viewBox="0 0 24 24">
              <rect x="2" y="4" width="8.5" height="16" rx="1" />
              <rect x="13.5" y="4" width="8.5" height="16" rx="1" />
            </svg>
          </button>

          {/* 1 column (Large) */}
          <button
            onClick={() => setThumbnailSize(8)}
            className={`flex-1 px-4 py-2 rounded-lg transition-all border-2 ${
              thumbnailSize >= 7
                ? 'bg-indigo-600 dark:bg-indigo-500 border-indigo-600 dark:border-indigo-500 text-white shadow-md'
                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
            }`}
            title="Large (1 column)"
          >
            <svg className="w-6 h-6 mx-auto" fill="currentColor" viewBox="0 0 24 24">
              <rect x="2" y="4" width="20" height="16" rx="1" />
            </svg>
          </button>
        </div>

        {/* Desktop: Slider (original) */}
        <div className="hidden md:flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
            Thumbnail Size:
          </label>
          <div className="flex items-center gap-3 flex-1 max-w-md">
            <span className="text-xs text-gray-500 dark:text-gray-400">Tiny</span>
            <div className="flex-1 relative">
              <input
                type="range"
                min="0"
                max="6"
                value={sizeToSlider[thumbnailSize]}
                onChange={(e) => setThumbnailSize(sliderToSize[Number(e.target.value)])}
                className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              />
              {/* Tick marks with column counts - aligned with slider stops */}
              <div className="absolute top-4 left-0 right-0 flex justify-between px-1 pointer-events-none">
                {sliderToSize.map((size) => (
                  <div key={size} className="flex flex-col items-center">
                    <div className="w-px h-1 bg-gray-400 dark:bg-gray-500"></div>
                    <span className="text-[9px] text-gray-400 dark:text-gray-500 mt-0.5">
                      {getDesktopColumns(size)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Full</span>
          </div>
          <span className="text-xs text-gray-600 dark:text-gray-400 font-medium min-w-[100px]">
            {thumbnailSize === 1 && `Tiny (${getDesktopColumns(1)} cols)`}
            {thumbnailSize === 2 && `Small (${getDesktopColumns(2)} cols)`}
            {thumbnailSize === 3 && `Medium (${getDesktopColumns(3)} cols)`}
            {thumbnailSize === 4 && `Large (${getDesktopColumns(4)} cols)`}
            {thumbnailSize === 5 && `X-Large (${getDesktopColumns(5)} cols)`}
            {thumbnailSize === 6 && `XX-Large (${getDesktopColumns(6)} cols)`}
            {thumbnailSize === 7 && `Huge (${getDesktopColumns(7)} cols)`}
            {thumbnailSize === 8 && `Full (${getDesktopColumns(8)} col)`}
          </span>
        </div>
      </div>
    </div>
  );
}

export default ContactSheetContent;
